/**
 * Global 401 interceptor — installed once at app boot.
 *
 * Wraps `window.fetch` so that ANY response with status 401 from a
 * same-origin `/api/...` endpoint dispatches a `auth:expired` custom
 * event on `window`. AppRouter listens for that event and runs its
 * regular `handleLogout` (clears storage + redirects to `/`).
 *
 * Why this matters:
 *   Every admin/customer screen calls `fetch` directly with a bearer
 *   token kept in React state. When the session epoch is bumped server
 *   side (password change, MFA reset, admin "invalidate sessions"),
 *   the next API call returns 401 — without this interceptor the UI
 *   would just show a generic "HTTP 401" banner on whatever page the
 *   user is on and pretend the app is still logged in.
 *
 * Endpoints whose 401 is EXPECTED behaviour (login attempts, MFA
 * challenges, the auth-status probe etc.) opt out via the
 * `EXEMPT_PATTERNS` list below — we never want a wrong-password
 * attempt to silently log the user out of an existing session.
 */

const EXEMPT_PATTERNS = [
  // Login + registration: 401 here means "wrong creds" and is a NORMAL
  // negative path, not a session expiry. The form handles it locally.
  /\/api\/auth\/login\b/,
  /\/api\/auth\/register\b/,
  /\/api\/auth\/forgot-password\b/,
  /\/api\/auth\/reset-password\b/,
  /\/api\/auth\/verify-email\b/,
  /\/api\/auth\/mfa\b/,                       // MFA challenge endpoints
  /\/api\/auth\/step-up\b/,                   // step-up MFA used for sensitive actions
  // /me is polled every 20s — when it 401s the user IS logged out, so
  // we DO want to react to that one. Leave it OUT of the exempt list.
];

const isExempt = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return EXEMPT_PATTERNS.some((rx) => rx.test(path));
  } catch (_) {
    return false;
  }
};

const isApi = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return path.startsWith('/api/');
  } catch (_) {
    return false;
  }
};

export function install401Interceptor() {
  if (typeof window === 'undefined' || window.__authInterceptorInstalled) return;
  window.__authInterceptorInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await originalFetch(...args);
    try {
      // Args can be (url, init) or (Request).
      const target = args[0];
      const url = typeof target === 'string' ? target : (target && target.url) || '';
      if (res.status === 401 && url && isApi(url) && !isExempt(url)) {
        // Cloning isn't strictly necessary here since we don't read the
        // body — but it costs nothing and shields any later body-read
        // in user code from "ReadableStream already locked" issues.
        window.dispatchEvent(new CustomEvent('auth:expired', { detail: { url } }));
      }
    } catch (_) { /* never let the interceptor break the original fetch */ }
    return res;
  };
}
